import { useCallback, useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Navigate } from "react-router";
import { Check, ChevronDown, Loader2, Plus, Sparkles, Trash2, Upload } from "lucide-react";
import { parseTypographyStyleClaim, toDisplayFontFamilyName } from "@runcheetah/shared";

import { CenteredLoader, EmptyCard, PageIntro } from "@/components/common";
import { getUnavailableBrandPath, useRouteBrand } from "@/hooks/use-route-brand";
import { getLogoPreviewCarrierFill } from "@/lib/brand-onboarding-display";
import { trpc } from "@/lib/trpc";
import { Button, Card, CardContent, CardHeader, CardTitle } from "@runcheetah/ui";
import { toast } from "sonner";

type SaveState = "idle" | "saving" | "error";
type PendingLogoChange = { type: "primary"; logoId: string };
type PendingLogoDelete = { logoId: string };

const HEX_COLOR_PATTERN = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;
const LOGO_UPLOAD_ACCEPT = "image/png,image/jpeg,image/webp,image/svg+xml";
const MAX_LOGO_UPLOAD_BYTES = 5 * 1024 * 1024;

const normalizeColorValue = (value: string): string => {
  const trimmed = value.trim();
  return HEX_COLOR_PATTERN.test(trimmed) ? trimmed.toUpperCase() : "#000000";
};

function ColorSwatch({
  color,
  onRemove,
  disabled = false,
}: {
  color: string;
  onRemove?: () => void;
  disabled?: boolean;
}) {
  const intl = useIntl();
  return (
    <div className="w-fit space-y-1.5 text-center">
      <div className="group relative mx-auto aspect-square w-full max-w-14 sm:max-w-16">
        <div className="h-full w-full rounded-xl" style={{ backgroundColor: color }} />
        {onRemove ? (
          <button
            type="button"
            onClick={onRemove}
            disabled={disabled}
            className="absolute inset-0 flex items-center justify-center rounded-xl bg-black/40 text-white opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100 disabled:opacity-0"
            aria-label={intl.formatMessage(
              { id: "brandStyle.removeColorAria", defaultMessage: "Remove {color}" },
              { color },
            )}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        ) : null}
      </div>
      <span className="font-mono text-[10px] text-muted-foreground">{color}</span>
    </div>
  );
}

function EditableColorPalette({
  colors,
  title,
  emptyMessage,
  colorPickerAriaLabel,
  addColorAriaLabel,
  onSave,
}: {
  colors: string[];
  title: string;
  emptyMessage: string;
  colorPickerAriaLabel: string;
  addColorAriaLabel: string;
  onSave: (colors: string[]) => Promise<unknown> | unknown;
}) {
  const intl = useIntl();
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [pendingColor, setPendingColor] = useState<string | null>(null);
  const colorInputRef = useRef<HTMLInputElement>(null);

  const persist = async (nextColors: string[]) => {
    setSaveState("saving");

    try {
      await onSave(nextColors);
      setSaveState("idle");
      toast.success(
        intl.formatMessage({ id: "brandStyle.colorsSaved", defaultMessage: "Colors saved" }),
      );
    } catch {
      setSaveState("error");
    }
  };

  const confirmAddColor = async () => {
    if (!pendingColor || colors.includes(pendingColor)) {
      return;
    }

    await persist([...colors, pendingColor]);
    setPendingColor(null);
  };

  const removeColor = async (color: string) => {
    await persist(colors.filter((entry) => entry !== color));
  };

  return (
    <div className="space-y-2.5">
      <div className="flex items-center justify-between gap-3">
        <CardTitle className="text-base">{title}</CardTitle>
        <div className="relative">
          <input
            ref={colorInputRef}
            type="color"
            defaultValue="#000000"
            className="pointer-events-none absolute top-full right-0 mt-2 h-8 w-8 opacity-0"
            aria-label={colorPickerAriaLabel}
            onChange={(event) => {
              setPendingColor(normalizeColorValue(event.target.value));
              setSaveState("idle");
            }}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 rounded-full"
            onClick={() => colorInputRef.current?.click()}
            disabled={saveState === "saving"}
            aria-label={addColorAriaLabel}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {colors.length ? (
        <div className="flex flex-wrap gap-2">
          {colors.map((color) => (
            <ColorSwatch
              key={color}
              color={color}
              onRemove={() => void removeColor(color)}
              disabled={saveState === "saving"}
            />
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">{emptyMessage}</p>
      )}

      <div className="min-h-7">
        {pendingColor ? (
          <div className="flex flex-wrap items-center gap-2.5">
            <div
              className="h-7 w-7 shrink-0 rounded-lg"
              style={{ backgroundColor: pendingColor }}
              aria-hidden="true"
            />
            <span className="min-w-0 font-mono text-xs text-muted-foreground">{pendingColor}</span>
            <Button
              type="button"
              size="sm"
              onClick={() => void confirmAddColor()}
              disabled={saveState === "saving" || colors.includes(pendingColor)}
            >
              <FormattedMessage id="common.save" defaultMessage="Save" />
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setPendingColor(null)}
              disabled={saveState === "saving"}
            >
              <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
            </Button>

            {colors.includes(pendingColor) ? (
              <p className="w-full text-xs text-muted-foreground">
                <FormattedMessage
                  id="brandStyle.duplicateColor"
                  defaultMessage="That color is already in the palette."
                />
              </p>
            ) : null}
          </div>
        ) : null}
      </div>

      {saveState !== "idle" ? (
        <p className="text-xs text-muted-foreground">
          {saveState === "saving" ? (
            <FormattedMessage id="common.saving" defaultMessage="Saving..." />
          ) : null}
          {saveState === "error" ? (
            <FormattedMessage
              id="common.saveErrorTryAgain"
              defaultMessage="Could not save. Try again."
            />
          ) : null}
        </p>
      ) : null}
    </div>
  );
}

export default function BrandStylePage() {
  const intl = useIntl();
  const { brand, brandQuery } = useRouteBrand();
  const utils = trpc.useUtils();
  const initializedTypographyBrandIdRef = useRef<string | null>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const uploadedLogosScrollRef = useRef<HTMLDivElement | null>(null);

  const query = trpc.brand.getVisualStyle.useQuery(
    { brandId: brand?.id ?? "" },
    { enabled: Boolean(brand?.id), staleTime: 0 },
  );
  const logosQuery = trpc.brand.listLogos.useQuery(
    { brandId: brand?.id ?? "" },
    { enabled: Boolean(brand?.id), staleTime: 0 },
  );
  const visualIdentityRegenerationQuery = trpc.brand.getVisualIdentityRegenerationStatus.useQuery(
    { brandId: brand?.id ?? "" },
    {
      enabled: Boolean(brand?.id),
      refetchInterval: (statusQuery) =>
        statusQuery.state.data?.state === "queued" || statusQuery.state.data?.state === "processing"
          ? 3000
          : false,
    },
  );
  const mutation = trpc.brand.updateBrandVoice.useMutation({
    onSuccess: async () => {
      if (!brand?.id) return;

      await Promise.all([
        utils.brand.getVisualStyle.invalidate({ brandId: brand.id }),
        utils.brand.getStrategyRegenerationStatus.invalidate({ brandId: brand.id }),
      ]);
    },
  });
  const createLogoUpload = trpc.brand.createLogoUpload.useMutation();
  const completeLogoUpload = trpc.brand.completeLogoUpload.useMutation({
    onSuccess: async () => {
      if (!brand?.id) return;

      await Promise.all([
        utils.brand.getVisualStyle.invalidate({ brandId: brand.id }),
        utils.brand.listLogos.invalidate({ brandId: brand.id }),
        utils.brand.getVisualIdentityRegenerationStatus.invalidate({ brandId: brand.id }),
      ]);
    },
  });
  const setPrimaryLogo = trpc.brand.setPrimaryLogo.useMutation({
    onSuccess: async () => {
      if (!brand?.id) return;

      await Promise.all([
        utils.brand.getVisualStyle.invalidate({ brandId: brand.id }),
        utils.brand.listLogos.invalidate({ brandId: brand.id }),
        utils.brand.getVisualIdentityRegenerationStatus.invalidate({ brandId: brand.id }),
      ]);
    },
  });
  const deleteLogo = trpc.brand.deleteLogo.useMutation({
    onSuccess: async () => {
      if (!brand?.id) return;

      await utils.brand.listLogos.invalidate({ brandId: brand.id });
    },
  });
  const [primaryPaletteDraft, setPrimaryPaletteDraft] = useState<string[]>([]);
  const [secondaryPaletteDraft, setSecondaryPaletteDraft] = useState<string[]>([]);
  const [isTypographyCollapsed, setIsTypographyCollapsed] = useState(false);
  const [uploadingLogoName, setUploadingLogoName] = useState<string | null>(null);
  const [pendingLogoChange, setPendingLogoChange] = useState<PendingLogoChange | null>(null);
  const [pendingLogoDelete, setPendingLogoDelete] = useState<PendingLogoDelete | null>(null);
  const [showAllUploadedLogos, setShowAllUploadedLogos] = useState(false);
  const [uploadedLogosCanScrollFurther, setUploadedLogosCanScrollFurther] = useState(false);

  useEffect(() => {
    setPrimaryPaletteDraft(
      (query.data?.colorPalette ?? []).map((color) => normalizeColorValue(color)),
    );
  }, [query.data?.colorPalette]);
  useEffect(() => {
    setSecondaryPaletteDraft(
      (query.data?.secondaryColorPalette ?? []).map((color) => normalizeColorValue(color)),
    );
  }, [query.data?.secondaryColorPalette]);

  const data = query.data;
  const uploadedLogos = logosQuery.data?.logos ?? [];
  const hasMoreUploadedLogos = uploadedLogos.length > 3;
  const visibleUploadedLogos =
    showAllUploadedLogos || !hasMoreUploadedLogos ? uploadedLogos : uploadedLogos.slice(0, 3);

  const updateUploadedLogosScrollHint = useCallback(() => {
    const element = uploadedLogosScrollRef.current;
    if (!element || !showAllUploadedLogos) {
      setUploadedLogosCanScrollFurther(false);
      return;
    }

    setUploadedLogosCanScrollFurther(
      element.scrollTop + element.clientHeight < element.scrollHeight - 2,
    );
  }, [showAllUploadedLogos]);

  useEffect(() => {
    updateUploadedLogosScrollHint();
  }, [uploadedLogos.length, updateUploadedLogosScrollHint]);

  const primaryUploadedLogo = uploadedLogos.find((logo) => logo.isPrimary) ?? null;
  const displayedLogoUrl = primaryUploadedLogo?.signedUrl ?? data?.logoUrl ?? null;
  const typography = data?.typographyStyle ? parseTypographyStyleClaim(data.typographyStyle) : null;
  const primaryColors = data?.colorPalette ?? [];
  const secondaryColors = data?.secondaryColorPalette ?? [];
  const headingFamilies = typography?.families.filter((family) => family.role !== "body") ?? [];
  const bodyFamilies = typography?.families.filter((family) => family.role !== "headings") ?? [];
  const logoCarrierFill = getLogoPreviewCarrierFill(data);
  const totalTypographyFontCount = headingFamilies.length + bodyFamilies.length;
  const styleSignals = [data?.visualMood, data?.imageStyle, data?.logoStyle].filter(
    Boolean,
  ) as string[];
  const hasAnyStyleData =
    Boolean(displayedLogoUrl) ||
    uploadedLogos.length > 0 ||
    Boolean(data?.visualIdentityDescription) ||
    primaryColors.length > 0 ||
    secondaryColors.length > 0 ||
    Boolean(typography) ||
    styleSignals.length > 0 ||
    Boolean(data?.designConsistencyLevel);
  const logoUploadBusy =
    createLogoUpload.isPending || completeLogoUpload.isPending || uploadingLogoName !== null;
  const logoChangeBusy = logoUploadBusy || setPrimaryLogo.isPending;
  const logoDeleteBusy = deleteLogo.isPending;
  const visualIdentityRegenerationState = visualIdentityRegenerationQuery.data?.state ?? "idle";
  const visualIdentityRegenerating =
    visualIdentityRegenerationState === "queued" ||
    visualIdentityRegenerationState === "processing";

  const handleLogoFileSelected = (file: File | undefined) => {
    if (!brand?.id || !file) return;

    if (!LOGO_UPLOAD_ACCEPT.split(",").includes(file.type)) {
      toast.error(
        intl.formatMessage({
          id: "brandStyle.logoUploadUnsupported",
          defaultMessage: "Upload a PNG, JPG, WebP, or SVG logo.",
        }),
      );
      return;
    }

    if (file.size > MAX_LOGO_UPLOAD_BYTES) {
      toast.error(
        intl.formatMessage({
          id: "brandStyle.logoUploadTooLarge",
          defaultMessage: "Logo files must be 5 MB or smaller.",
        }),
      );
      return;
    }

    void uploadLogoFile(file);
  };

  const uploadLogoFile = async (file: File) => {
    if (!brand?.id) return;

    setUploadingLogoName(file.name);
    try {
      const upload = await createLogoUpload.mutateAsync({
        brandId: brand.id,
        fileName: file.name,
        contentType: file.type as "image/png" | "image/jpeg" | "image/webp" | "image/svg+xml",
        sizeBytes: file.size,
      });
      const response = await fetch(upload.uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": upload.contentType },
        body: file,
      });

      if (!response.ok) {
        throw new Error(`Logo upload failed with status ${response.status}`);
      }

      await completeLogoUpload.mutateAsync({
        brandId: brand.id,
        objectKey: upload.objectKey,
        fileName: file.name,
        contentType: upload.contentType,
        sizeBytes: file.size,
        makePrimary: false,
        regenerateVisualIdentity: false,
      });
      toast.success(
        intl.formatMessage({
          id: "brandStyle.logoUploaded",
          defaultMessage: "Logo uploaded",
        }),
      );
    } catch {
      toast.error(
        intl.formatMessage({
          id: "brandStyle.logoUploadFailed",
          defaultMessage: "Could not upload the logo. Try again.",
        }),
      );
    } finally {
      setUploadingLogoName(null);
      if (logoInputRef.current) {
        logoInputRef.current.value = "";
      }
    }
  };

  const confirmLogoChange = async () => {
    if (!brand?.id || !pendingLogoChange) return;

    try {
      await setPrimaryLogo.mutateAsync({
        brandId: brand.id,
        logoId: pendingLogoChange.logoId,
        regenerateVisualIdentity: true,
      });
      toast.success(
        intl.formatMessage({
          id: "brandStyle.primaryLogoUpdatedRegenerating",
          defaultMessage: "Primary logo updated. Regenerating visual identity.",
        }),
      );

      setPendingLogoChange(null);
    } catch {
      toast.error(
        intl.formatMessage({
          id: "brandStyle.logoChangeFailed",
          defaultMessage: "Could not update the logo. Try again.",
        }),
      );
    }
  };

  const cancelLogoChange = () => {
    setPendingLogoChange(null);
    if (logoInputRef.current) {
      logoInputRef.current.value = "";
    }
  };

  const confirmLogoDelete = async () => {
    if (!brand?.id || !pendingLogoDelete) return;

    try {
      await deleteLogo.mutateAsync({ brandId: brand.id, logoId: pendingLogoDelete.logoId });
      toast.success(
        intl.formatMessage({ id: "brandStyle.logoDeleted", defaultMessage: "Logo deleted" }),
      );
      setPendingLogoDelete(null);
    } catch {
      toast.error(
        intl.formatMessage({
          id: "brandStyle.logoDeleteFailed",
          defaultMessage: "Could not delete the logo. Try again.",
        }),
      );
    }
  };

  useEffect(() => {
    if (!brand?.id || initializedTypographyBrandIdRef.current === brand.id) {
      return;
    }

    initializedTypographyBrandIdRef.current = brand.id;
    setIsTypographyCollapsed(totalTypographyFontCount > 6);
  }, [brand?.id, totalTypographyFontCount]);

  if (brandQuery.isPending || query.isPending) {
    return <CenteredLoader />;
  }

  if (!brand || brand.status !== "scanned") {
    return <Navigate to={getUnavailableBrandPath(brand?.id)} replace />;
  }

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6 px-6 py-6">
      <PageIntro
        title={intl.formatMessage({ id: "brandStyle.title", defaultMessage: "Visual Style" })}
        titleClassName="text-balance"
      />

      {!hasAnyStyleData ? (
        <EmptyCard className="border-border/70 bg-card/90 shadow-sm">
          <FormattedMessage
            id="brandStyle.empty"
            defaultMessage="We have not detected enough visual style data yet. Add more source material or let the scan finish, then check back here."
          />
        </EmptyCard>
      ) : null}

      <div className="grid gap-5 lg:grid-cols-[minmax(22rem,30rem)_minmax(20rem,26rem)]">
        <Card className="border-border/70 bg-card/90 shadow-sm">
          <CardHeader className="flex flex-row items-start justify-between gap-3 px-4 pt-4 pb-3">
            <div className="space-y-1">
              <CardTitle className="text-base">
                <FormattedMessage
                  id="brandStyle.logoIdentityTitle"
                  defaultMessage="Logo & Identity"
                />
              </CardTitle>
              {primaryUploadedLogo ? (
                <p className="text-xs text-muted-foreground">
                  <FormattedMessage
                    id="brandStyle.primaryUploadedLogo"
                    defaultMessage="Using uploaded primary logo"
                  />
                </p>
              ) : null}
            </div>
            <input
              ref={logoInputRef}
              type="file"
              accept={LOGO_UPLOAD_ACCEPT}
              className="hidden"
              onChange={(event) => {
                void handleLogoFileSelected(event.currentTarget.files?.[0]);
              }}
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="shrink-0 gap-1.5"
              disabled={logoUploadBusy}
              onClick={() => logoInputRef.current?.click()}
            >
              <Upload className="h-3.5 w-3.5" aria-hidden="true" />
              {uploadingLogoName ? (
                <FormattedMessage id="brandStyle.uploadingLogo" defaultMessage="Uploading" />
              ) : (
                <FormattedMessage id="brandStyle.uploadLogo" defaultMessage="Upload" />
              )}
            </Button>
          </CardHeader>
          <CardContent className="flex h-full flex-col space-y-4 px-4 pb-4">
            {displayedLogoUrl ? (
              <div
                className="flex min-h-48 flex-1 items-center justify-center rounded-2xl border border-border p-5 sm:min-h-56 sm:p-6"
                style={{ backgroundColor: logoCarrierFill }}
              >
                <img
                  src={displayedLogoUrl}
                  alt={intl.formatMessage({
                    id: "brandStyle.brandLogoAlt",
                    defaultMessage: "Brand logo",
                  })}
                  className="max-h-32 w-full object-contain sm:max-h-40"
                />
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                <FormattedMessage id="brandStyle.noLogo" defaultMessage="No logo detected yet." />
              </p>
            )}

            {uploadedLogos.length > 0 ? (
              <div className="space-y-3 border-t border-border/70 pt-4">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">
                    <FormattedMessage
                      id="brandStyle.uploadedLogosTitle"
                      defaultMessage="Uploaded logos"
                    />
                  </p>
                  <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                    <FormattedMessage
                      id="brandStyle.uploadedLogosCount"
                      defaultMessage="{count, plural, one {# logo} other {# logos}}"
                      values={{ count: uploadedLogos.length }}
                    />
                  </span>
                </div>
                <div className="relative">
                  <div
                    ref={uploadedLogosScrollRef}
                    className={
                      showAllUploadedLogos ? "max-h-64 space-y-2 overflow-y-auto pr-1" : "space-y-2"
                    }
                    onScroll={updateUploadedLogosScrollHint}
                  >
                    {visibleUploadedLogos.map((logo) => (
                      <div
                        key={logo.id}
                        className={`flex items-center gap-3 rounded-2xl border p-2 transition-colors ${
                          logo.isPrimary
                            ? "border-primary/30 bg-primary/[0.04]"
                            : "border-border/70 bg-background/55"
                        }`}
                      >
                        <div className="flex h-14 w-20 shrink-0 items-center justify-center rounded-xl bg-muted/60 p-2">
                          <img
                            src={logo.signedUrl}
                            alt={
                              logo.label ??
                              intl.formatMessage({
                                id: "brandStyle.logoAlt",
                                defaultMessage: "Uploaded logo",
                              })
                            }
                            className="max-h-full max-w-full object-contain"
                            loading="lazy"
                          />
                        </div>
                        <div className="min-w-0 flex-1 space-y-1">
                          <p className="truncate text-xs font-medium text-foreground">
                            {logo.label ??
                              intl.formatMessage({
                                id: "brandStyle.untitledLogo",
                                defaultMessage: "Logo",
                              })}
                          </p>
                          {logo.isPrimary ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-1 text-[10px] font-medium text-primary">
                              <Check className="h-3 w-3" aria-hidden="true" />
                              <FormattedMessage
                                id="brandStyle.primaryLogo"
                                defaultMessage="Primary"
                              />
                            </span>
                          ) : (
                            <div className="flex flex-wrap items-center gap-1.5">
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                className="h-7 px-2 text-[11px] text-muted-foreground hover:text-foreground"
                                disabled={setPrimaryLogo.isPending || logoDeleteBusy}
                                onClick={() => {
                                  if (!brand?.id) return;
                                  setPendingLogoChange({ type: "primary", logoId: logo.id });
                                }}
                              >
                                <FormattedMessage
                                  id="brandStyle.setPrimaryLogo"
                                  defaultMessage="Make primary"
                                />
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                className="h-7 px-2 text-[11px] text-destructive/75 hover:text-destructive"
                                disabled={logoDeleteBusy || logoChangeBusy}
                                onClick={() => setPendingLogoDelete({ logoId: logo.id })}
                              >
                                <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                                <FormattedMessage
                                  id="brandStyle.deleteLogo"
                                  defaultMessage="Delete"
                                />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  {uploadedLogosCanScrollFurther ? (
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 rounded-b-2xl bg-gradient-to-t from-card via-card/85 to-transparent" />
                  ) : null}
                </div>
                {hasMoreUploadedLogos ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-8 w-full rounded-full text-xs text-muted-foreground hover:text-foreground"
                    onClick={() => setShowAllUploadedLogos((current) => !current)}
                  >
                    {showAllUploadedLogos ? (
                      <FormattedMessage id="brandStyle.showFewerLogos" defaultMessage="Show less" />
                    ) : (
                      <FormattedMessage
                        id="brandStyle.showMoreLogos"
                        defaultMessage="Show {count} more"
                        values={{ count: uploadedLogos.length - 3 }}
                      />
                    )}
                  </Button>
                ) : null}
              </div>
            ) : null}

            {!data?.logoUrl && !data?.visualIdentityDescription ? (
              <p className="text-sm text-muted-foreground">
                <FormattedMessage
                  id="brandStyle.noLogoSignal"
                  defaultMessage="We have not found a strong logo or identity signal yet."
                />
              </p>
            ) : null}
          </CardContent>
        </Card>

        <Card className="self-start border-border/70 bg-card/90 shadow-sm">
          <CardContent className="p-3.5 sm:p-4">
            <div className="relative" aria-busy={visualIdentityRegenerating}>
              {visualIdentityRegenerating ? (
                <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
                  <p className="inline-flex items-center gap-2 rounded-full bg-background/75 px-3 py-1.5 text-xs font-medium text-foreground shadow-sm ring-1 ring-border/60 backdrop-blur-md">
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" aria-hidden="true" />
                    <FormattedMessage
                      id="brandStyle.visualIdentityGeneratingTitle"
                      defaultMessage="Regenerating palette"
                    />
                  </p>
                </div>
              ) : null}

              <div
                className={`space-y-4 transition duration-500 ${
                  visualIdentityRegenerating
                    ? "pointer-events-none select-none blur-[2px] opacity-55"
                    : ""
                }`}
              >
                <EditableColorPalette
                  colors={primaryPaletteDraft}
                  title={intl.formatMessage({
                    id: "brandStyle.primaryColorProfile.title",
                    defaultMessage: "Primary Color Profile",
                  })}
                  emptyMessage={intl.formatMessage({
                    id: "brandStyle.primaryColorProfile.empty",
                    defaultMessage: "No primary color palette data is available yet.",
                  })}
                  colorPickerAriaLabel={intl.formatMessage({
                    id: "brandStyle.primaryColorProfile.colorPickerAria",
                    defaultMessage: "Pick a primary brand color",
                  })}
                  addColorAriaLabel={intl.formatMessage({
                    id: "brandStyle.primaryColorProfile.addColorAria",
                    defaultMessage: "Add primary color",
                  })}
                  onSave={async (nextColors) => {
                    if (!brand?.id) return;

                    const normalizedColors = nextColors.map((color) => normalizeColorValue(color));
                    setPrimaryPaletteDraft(normalizedColors);

                    await mutation
                      .mutateAsync({
                        brandId: brand.id,
                        fields: { "visualIdentitySnapshot.colorPalette": normalizedColors },
                      })
                      .catch(() => {
                        toast.error(
                          intl.formatMessage({
                            id: "brandStyle.couldNotSaveColors",
                            defaultMessage: "Could not save colors",
                          }),
                        );
                        throw new Error("save_failed");
                      });
                  }}
                />
                <EditableColorPalette
                  colors={secondaryPaletteDraft}
                  title={intl.formatMessage({
                    id: "brandStyle.secondaryColorProfile.title",
                    defaultMessage: "Secondary Color Profile",
                  })}
                  emptyMessage={intl.formatMessage({
                    id: "brandStyle.secondaryColorProfile.empty",
                    defaultMessage: "No secondary color palette data is available yet.",
                  })}
                  colorPickerAriaLabel={intl.formatMessage({
                    id: "brandStyle.secondaryColorProfile.colorPickerAria",
                    defaultMessage: "Pick a secondary brand color",
                  })}
                  addColorAriaLabel={intl.formatMessage({
                    id: "brandStyle.secondaryColorProfile.addColorAria",
                    defaultMessage: "Add secondary color",
                  })}
                  onSave={async (nextColors) => {
                    if (!brand?.id) return;

                    const normalizedColors = nextColors.map((color) => normalizeColorValue(color));
                    setSecondaryPaletteDraft(normalizedColors);

                    await mutation
                      .mutateAsync({
                        brandId: brand.id,
                        fields: {
                          "visualIdentitySnapshot.secondaryColorPalette": normalizedColors,
                        },
                      })
                      .catch(() => {
                        toast.error(
                          intl.formatMessage({
                            id: "brandStyle.couldNotSaveColors",
                            defaultMessage: "Could not save colors",
                          }),
                        );
                        throw new Error("save_failed");
                      });
                  }}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="w-full max-w-[51.25rem] border-border/70 bg-card/90 shadow-sm lg:mx-0">
        <CardHeader className="">
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="text-lg">
              <FormattedMessage id="brandStyle.typographyTitle" defaultMessage="Typography" />
            </CardTitle>
            <button
              type="button"
              onClick={() => setIsTypographyCollapsed((current) => !current)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground"
              aria-label={
                isTypographyCollapsed
                  ? intl.formatMessage({
                      id: "brandStyle.expandTypography",
                      defaultMessage: "Expand typography",
                    })
                  : intl.formatMessage({
                      id: "brandStyle.collapseTypography",
                      defaultMessage: "Collapse typography",
                    })
              }
              aria-expanded={!isTypographyCollapsed}
            >
              <ChevronDown
                className={`h-4 w-4 transition-transform ${isTypographyCollapsed ? "-rotate-90" : "rotate-0"}`}
              />
            </button>
          </div>
        </CardHeader>
        {!isTypographyCollapsed ? (
          <CardContent>
            {typography ? (
              <div className="space-y-5">
                {typography.summary ? (
                  <p className="text-sm leading-6 text-muted-foreground">{typography.summary}</p>
                ) : null}
                <div className="grid gap-5 lg:grid-cols-2">
                  <div className="space-y-3">
                    <p className="text-sm font-medium text-foreground">
                      <FormattedMessage
                        id="brandStyle.headingFonts"
                        defaultMessage="Heading Fonts"
                      />
                    </p>
                    {headingFamilies.length ? (
                      headingFamilies.map((family) => (
                        <div
                          key={`heading-${family.family}`}
                          className="rounded-2xl border border-border/70 bg-background/55 px-4 py-3"
                        >
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-sm font-medium text-foreground">
                              {toDisplayFontFamilyName(family.family)}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {family.variants.length ? (
                                family.variants.join(", ")
                              ) : (
                                <FormattedMessage id="common.default" defaultMessage="Default" />
                              )}
                            </span>
                          </div>
                          <p
                            className="mt-3 text-2xl text-foreground"
                            style={{ fontFamily: `${family.family}, var(--font-sans)` }}
                            aria-hidden="true"
                          >
                            Aa
                          </p>
                          <p
                            className="text-sm text-muted-foreground"
                            style={{ fontFamily: `${family.family}, var(--font-sans)` }}
                          >
                            <FormattedMessage
                              id="brandStyle.fontSample"
                              defaultMessage="The quick brown fox jumps over the lazy dog"
                            />
                          </p>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        <FormattedMessage
                          id="brandStyle.noHeadingFonts"
                          defaultMessage="No heading fonts detected yet."
                        />
                      </p>
                    )}
                  </div>

                  <div className="space-y-3">
                    <p className="text-sm font-medium text-foreground">
                      <FormattedMessage id="brandStyle.bodyFonts" defaultMessage="Body Fonts" />
                    </p>
                    {bodyFamilies.length ? (
                      bodyFamilies.map((family) => (
                        <div
                          key={`body-${family.family}`}
                          className="rounded-2xl border border-border/70 bg-background/55 px-4 py-3"
                        >
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-sm font-medium text-foreground">
                              {toDisplayFontFamilyName(family.family)}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {family.variants.length ? (
                                family.variants.join(", ")
                              ) : (
                                <FormattedMessage id="common.default" defaultMessage="Default" />
                              )}
                            </span>
                          </div>
                          <p
                            className="mt-3 text-lg text-foreground"
                            style={{ fontFamily: `${family.family}, var(--font-sans)` }}
                            aria-hidden="true"
                          >
                            Aa
                          </p>
                          <p
                            className="text-sm text-muted-foreground"
                            style={{ fontFamily: `${family.family}, var(--font-sans)` }}
                          >
                            <FormattedMessage
                              id="brandStyle.fontSample"
                              defaultMessage="The quick brown fox jumps over the lazy dog"
                            />
                          </p>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        <FormattedMessage
                          id="brandStyle.noBodyFonts"
                          defaultMessage="No body fonts detected yet."
                        />
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                <FormattedMessage
                  id="brandStyle.noTypography"
                  defaultMessage="No typography data is available yet."
                />
              </p>
            )}
          </CardContent>
        ) : null}
      </Card>

      {pendingLogoChange ? (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-background/75 p-4 backdrop-blur-sm">
          <Card className="w-full max-w-md rounded-3xl border-border/70 shadow-xl">
            <CardContent className="space-y-5 p-6">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                  <Sparkles className="h-5 w-5" aria-hidden="true" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold tracking-tight text-foreground">
                    <FormattedMessage
                      id="brandStyle.logoRegenerationConfirmTitle"
                      defaultMessage="Regenerate visual identity?"
                    />
                  </h2>
                  <p className="mt-1 text-sm leading-6 text-muted-foreground">
                    <FormattedMessage
                      id="brandStyle.logoRegenerationConfirmDescription"
                      defaultMessage="Changing the primary logo will regenerate your visual identity so Cheetah can refresh the color palette from the new logo. This may take a few minutes."
                    />
                  </p>
                </div>
              </div>

              <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={cancelLogoChange}
                  disabled={logoChangeBusy}
                >
                  <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
                </Button>
                <Button
                  type="button"
                  onClick={() => void confirmLogoChange()}
                  disabled={logoChangeBusy}
                >
                  {logoChangeBusy ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  ) : null}
                  <FormattedMessage
                    id="brandStyle.logoRegenerationConfirmAction"
                    defaultMessage="Change logo and regenerate"
                  />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      ) : null}

      {pendingLogoDelete ? (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-background/75 p-4 backdrop-blur-sm">
          <Card className="w-full max-w-md rounded-3xl border-border/70 shadow-xl">
            <CardContent className="space-y-5 p-6">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-destructive/10 text-destructive">
                  <Trash2 className="h-5 w-5" aria-hidden="true" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold tracking-tight text-foreground">
                    <FormattedMessage
                      id="brandStyle.logoDeleteConfirmTitle"
                      defaultMessage="Delete this logo?"
                    />
                  </h2>
                  <p className="mt-1 text-sm leading-6 text-muted-foreground">
                    <FormattedMessage
                      id="brandStyle.logoDeleteConfirmDescription"
                      defaultMessage="This removes the logo from every asset that uses it. Those assets will fall back to your primary logo."
                    />
                  </p>
                </div>
              </div>

              <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setPendingLogoDelete(null)}
                  disabled={logoDeleteBusy}
                >
                  <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => void confirmLogoDelete()}
                  disabled={logoDeleteBusy}
                >
                  {logoDeleteBusy ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  ) : null}
                  <FormattedMessage
                    id="brandStyle.logoDeleteConfirmAction"
                    defaultMessage="Delete logo"
                  />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      ) : null}
    </div>
  );
}
